import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const PasswordResetRequestPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const { requestPasswordReset, error, isLoading, clearError, resetEmailSent, resetEmail, clearResetEmailSent } = useAuth();

  useEffect(() => {
    // Only clear errors when component mounts
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  const handlePasswordResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!email) {
      return;
    }

    console.log('Requesting password reset for:', email);
    const success = await requestPasswordReset(email);
    console.log('Password reset request success:', success);
  };

  const resetForm = () => {
    clearResetEmailSent();
    setEmail('');
    clearError();
    window.location.href = '/';
  };

  if (resetEmailSent) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card>
              <Card.Body className="text-center">
                <h4 className="mb-3">{t('auth.resetPassword.emailSent.title')}</h4>
                <p>
                  {t('auth.resetPassword.emailSent.message')} <strong>{resetEmail}</strong>.
                </p>
                <p className="text-muted small">
                  {t('auth.resetPassword.emailSent.checkSpam')}
                </p>
                <Button
                  variant="outline-primary"
                  onClick={resetForm}
                >
                  {t('auth.resetPassword.emailSent.backToLogin')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={4}>
          <Card>
            <Card.Body>
              <h4 className="text-center mb-4">{t('auth.resetPassword.requestTitle')}</h4>

              <div className="alert alert-info mb-4">
                <small>
                  {t('auth.resetPassword.requestDescription')}
                </small>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={clearError}>
                  <div>{error}</div>
                  {error === t('auth.errors.ERROR_ACCOUNT_NOT_FOUND') && (
                    <div className="mt-2">
                      <Button
                        variant="link"
                        className="p-0 text-decoration-underline"
                        onClick={() => window.location.href = '/account-aanvragen'}
                      >
                        {t('auth.resetPassword.goToAccountRequest')}
                      </Button>
                    </div>
                  )}
                </Alert>
              )}

              <Form onSubmit={handlePasswordResetRequest}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.emailLabel')}</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading || !email}
                  >
                    {isLoading ? t('auth.loading') : t('auth.resetPassword.requestButton')}
                  </Button>
                </div>
              </Form>

              <div className="text-center mt-3">
                <Button
                  variant="link"
                  onClick={() => window.location.href = '/'}
                  disabled={isLoading}
                >
                  {t('auth.backToLogin')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PasswordResetRequestPage;
